import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Bell, 
  Mail, 
  MessageSquare,
  Phone,
  Calendar,
  Clock,
  Loader2,
  ArrowLeft,
  Save,
  CheckCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const NotificationSettings = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    // Email notifications
    email_appointment_confirmation: true,
    email_appointment_reminder_7d: true,
    email_appointment_reminder_1d: true,
    email_appointment_reminder_2h: true,
    email_appointment_cancelled: true,
    email_new_message: true,
    email_promotions: false,
    email_newsletter: false,
    
    // SMS notifications
    sms_appointment_reminder_1d: true,
    sms_appointment_reminder_2h: true,
    sms_appointment_cancelled: true,
    
    // Push notifications (future)
    push_enabled: false,
    
    // Quiet hours
    quiet_hours_enabled: false,
    quiet_hours_start: "22:00",
    quiet_hours_end: "08:00"
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/users/notification-settings`, { headers });
      if (response.data.settings) {
        setSettings(prev => ({ ...prev, ...response.data.settings }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/users/notification-settings`,
        { settings },
        { headers }
      );
      toast.success('Préférences enregistrées');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const NotificationToggle = ({ settingKey, label, description, icon: Icon }) => (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-start gap-3">
        {Icon && <Icon className="w-5 h-5 text-slate-400 mt-0.5" />}
        <div>
          <Label htmlFor={settingKey} className="font-medium cursor-pointer">
            {label}
          </Label>
          {description && (
            <p className="text-sm text-slate-500">{description}</p>
          )}
        </div>
      </div>
      <Switch
        id={settingKey}
        checked={settings[settingKey]}
        onCheckedChange={(checked) => updateSetting(settingKey, checked)}
        data-testid={`toggle-${settingKey}`}
      />
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="notification-settings-page">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour au tableau de bord
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Bell className="w-8 h-8 text-primary" />
            Préférences de notifications
          </h1>
          <p className="text-slate-500 mt-1">
            Choisissez comment et quand vous souhaitez être notifié
          </p>
        </div>

        {/* Email Notifications */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-500" />
              Notifications par email
            </CardTitle>
            <CardDescription>
              Recevez des mises à jour importantes par email
            </CardDescription>
          </CardHeader>
          <CardContent className="divide-y">
            <NotificationToggle
              settingKey="email_appointment_confirmation"
              label="Confirmation de rendez-vous"
              description="Email immédiat après chaque réservation"
              icon={CheckCircle}
            />
            <NotificationToggle
              settingKey="email_appointment_reminder_7d"
              label="Rappel J-7"
              description="Rappel une semaine avant le rendez-vous"
              icon={Calendar}
            />
            <NotificationToggle
              settingKey="email_appointment_reminder_1d"
              label="Rappel J-1"
              description="Rappel la veille du rendez-vous"
              icon={Calendar}
            />
            <NotificationToggle
              settingKey="email_appointment_reminder_2h"
              label="Rappel H-2"
              description="Rappel 2 heures avant le rendez-vous"
              icon={Clock}
            />
            <NotificationToggle
              settingKey="email_appointment_cancelled"
              label="Annulation de rendez-vous"
              description="Notification en cas d'annulation"
              icon={Calendar}
            />
            <NotificationToggle
              settingKey="email_new_message"
              label="Nouveaux messages"
              description="Quand un professionnel vous envoie un message"
              icon={MessageSquare}
            />
            <div className="pt-4 border-t mt-4">
              <p className="text-sm font-medium text-slate-500 mb-3">Marketing</p>
              <NotificationToggle
                settingKey="email_promotions"
                label="Offres et promotions"
                description="Réductions et offres spéciales"
              />
              <NotificationToggle
                settingKey="email_newsletter"
                label="Newsletter santé"
                description="Conseils et actualités santé"
              />
            </div>
          </CardContent>
        </Card>

        {/* SMS Notifications */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-green-500" />
              Notifications SMS
              <Badge variant="outline" className="ml-2">Premium</Badge>
            </CardTitle>
            <CardDescription>
              Rappels par SMS pour ne jamais manquer un rendez-vous
            </CardDescription>
          </CardHeader>
          <CardContent className="divide-y">
            <NotificationToggle
              settingKey="sms_appointment_reminder_1d"
              label="Rappel SMS J-1"
              description="SMS la veille du rendez-vous"
              icon={Clock}
            />
            <NotificationToggle
              settingKey="sms_appointment_reminder_2h"
              label="Rappel SMS H-2"
              description="SMS 2 heures avant le rendez-vous"
              icon={Clock}
            />
            <NotificationToggle
              settingKey="sms_appointment_cancelled"
              label="Annulation par SMS"
              description="SMS en cas d'annulation urgente"
              icon={Calendar}
            />
          </CardContent>
        </Card>

        {/* Quiet Hours */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-500" />
              Heures calmes
            </CardTitle>
            <CardDescription>
              Désactivez les notifications pendant certaines heures
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between py-3">
              <div>
                <Label htmlFor="quiet_hours" className="font-medium">
                  Activer les heures calmes
                </Label>
                <p className="text-sm text-slate-500">
                  Pas de notifications de {settings.quiet_hours_start} à {settings.quiet_hours_end}
                </p>
              </div>
              <Switch
                id="quiet_hours"
                checked={settings.quiet_hours_enabled}
                onCheckedChange={(checked) => updateSetting('quiet_hours_enabled', checked)}
              />
            </div>
            
            {settings.quiet_hours_enabled && (
              <div className="flex gap-4 mt-4 p-4 bg-slate-50 rounded-lg">
                <div className="flex-1">
                  <Label htmlFor="quiet_start" className="text-sm">Début</Label>
                  <input
                    type="time"
                    id="quiet_start"
                    value={settings.quiet_hours_start}
                    onChange={(e) => updateSetting('quiet_hours_start', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border rounded-md"
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="quiet_end" className="text-sm">Fin</Label>
                  <input
                    type="time"
                    id="quiet_end"
                    value={settings.quiet_hours_end}
                    onChange={(e) => updateSetting('quiet_hours_end', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border rounded-md"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            onClick={saveSettings} 
            disabled={saving}
            className="gap-2"
            data-testid="save-notification-settings"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Enregistrer les préférences
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
